import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, Award, Loader2, ChevronLeft, ChevronRight, X, Maximize2,
  Printer, RefreshCw, Sparkles, CheckCircle2, Circle, AlertTriangle,
  Activity, ClipboardList, FileText, BarChart3, Calendar, Play, Pause, Languages
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PILLAR_ORDER = [
  'Safety Policy',
  'Safety Accountability, Authority & Responsibilities',
  'Safety Risk Management',
  'Safety Assurance',
  'Safety Promotion',
  'SMS Documentation & Recordkeeping',
];

// Translation key suffix for each pillar's short name
const PILLAR_KEY = {
  'Safety Policy': 'policy',
  'Safety Accountability, Authority & Responsibilities': 'accountability',
  'Safety Risk Management': 'risk',
  'Safety Assurance': 'assurance',
  'Safety Promotion': 'promotion',
  'SMS Documentation & Recordkeeping': 'docs',
};

const SLIDE_DEFS = [
  { id: 'cover', icon: ShieldCheck, labelKey: 'inspectorMode.cover.label' },
  { id: 'pillars', icon: BarChart3, labelKey: 'inspectorMode.pillars.label' },
  { id: 'items', icon: ClipboardList, labelKey: 'inspectorMode.items.label' },
  { id: 'capas', icon: Calendar, labelKey: 'inspectorMode.capas.label' },
];

// ---- Utility components ----
const PSOEPill = ({ label, on }) => (
  <div
    className={`px-3 py-1.5 rounded-md border text-xs flex items-center gap-1.5 ${
      on
        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
        : 'bg-slate-900 border-slate-700 text-slate-500'
    }`}
  >
    {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
    {label}
  </div>
);

const MaturityGauge = ({ name, pct, label, items, max, shortName }) => {
  const r = 56;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const stroke =
    pct >= 75 ? '#16a34a' :
    pct >= 50 ? '#0284c7' :
    pct >= 25 ? '#d97706' :
    '#dc2626';
  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-950/60 p-5 flex flex-col items-center"
      data-testid={`inspector-gauge-${PILLAR_KEY[name] || name}`}
    >
      <div className="relative w-32 h-32" dir="ltr">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={r} stroke="#1e293b" strokeWidth="8" fill="none" />
          <circle
            cx="64" cy="64" r={r}
            stroke={stroke} strokeWidth="8" fill="none"
            strokeDasharray={c} strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-white tabular-nums">{Math.round(pct)}%</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500" dir="ltr">{items}/{max}</div>
        </div>
      </div>
      <div className="mt-3 text-sm font-semibold text-white text-center">{shortName}</div>
      <Badge className="mt-1 bg-slate-900 border border-slate-800 text-[10px] text-slate-300">{label}</Badge>
    </div>
  );
};

// ---- Main page ----
export default function InspectorModePage({ onExit }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const pillarShort = (name) => t(`inspectorMode.pillars.${PILLAR_KEY[name] || 'policy'}`);
  const SLIDES = SLIDE_DEFS.map(s => ({ ...s, label: t(s.labelKey) }));
  const [slide, setSlide] = useState(0);
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [linked, setLinked] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [itemIdx, setItemIdx] = useState(0);
  const [pillarFilter, setPillarFilter] = useState('all');
  const [cursorHidden, setCursorHidden] = useState(false);
  const cursorTimer = useRef(null);

  // ---- On-the-fly narrative translation cache (Inspector Mode only) ----
  // Map<itemId, { lang: 'ar'|'en', text: string, cached: boolean, sourceText: string }>
  const [translations, setTranslations] = useState({});
  const [translatingItemId, setTranslatingItemId] = useState(null);

  // ---- Auto-pilot state (opt-in, never persisted as ON) ----
  const SPEED_OPTIONS = [15, 30, 60, 90];
  const [autoPilot, setAutoPilot] = useState(false);
  const [cycleSpeed, setCycleSpeed] = useState(() => {
    const saved = parseInt(localStorage.getItem('inspectorMode.cycleSpeed'), 10);
    return SPEED_OPTIONS.includes(saved) ? saved : 30;
  });
  const [tickRemaining, setTickRemaining] = useState(0);
  const tickRef = useRef(null);
  const refreshRef = useRef(null);
  const SILENT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

  const token = localStorage.getItem('token');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [c, s, r, l, m] = await Promise.all([
        fetch(`${API_BASE}/api/gaca-audit/checklist`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-audit/score`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-p1/readiness/summary`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-p1/readiness/linked-capas`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-audit/metadata`, { headers }).then(r => r.json()),
      ]);
      setData(c);
      setScore(s);
      setReadiness(r);
      setLinked(l);
      setMetadata(m);
    } catch {
      toast.error('Failed to load inspector data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // RTL: set <html dir> while Inspector Mode is mounted; restore on unmount
  useEffect(() => {
    const html = document.documentElement;
    const prevDir = html.getAttribute('dir');
    const prevLang = html.getAttribute('lang');
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    html.setAttribute('lang', i18n.language);
    return () => {
      // Restore — but if user keeps Arabic across pages, don't fight other components
      if (prevDir !== null) html.setAttribute('dir', prevDir);
      else html.setAttribute('dir', i18n.language === 'ar' ? 'rtl' : 'ltr');
      if (prevLang !== null) html.setAttribute('lang', prevLang);
    };
  }, [isRTL, i18n.language]);

  const toggleLanguage = () => {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
  };

  // Translate the narrative into the viewer's current UI language. The LLM
  // returns the source unchanged if it's already in the target language, so
  // the button is safe to click regardless of source language.
  const handleTranslate = useCallback(async (item) => {
    const sourceText = item?.assessment?.how_achieved;
    if (!sourceText || !sourceText.trim()) return;
    const itemId = item.id;
    const targetLang = i18n.language === 'ar' ? 'ar' : 'en';
    const existing = translations[itemId];
    // Toggle off if already showing the translation in this same lang+source
    if (
      existing &&
      existing.text &&
      existing.sourceText === sourceText &&
      existing.lang === targetLang
    ) {
      setTranslations(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }
    setTranslatingItemId(itemId);
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/translate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sourceText,
          target_lang: targetLang,
          item_id: itemId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTranslations(prev => ({
        ...prev,
        [itemId]: {
          lang: data.target_lang || targetLang,
          text: data.translated,
          cached: !!data.cached,
          sourceText,
        },
      }));
    } catch (e) {
      toast.error(t('inspectorMode.items.translateFailed') + (e?.message ? `: ${e.message}` : ''));
    } finally {
      setTranslatingItemId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations, t, token, i18n.language]);

  const filteredItems = data?.items?.filter(i =>
    pillarFilter === 'all' || i.pillar === pillarFilter
  ) || [];

  // Keep itemIdx in range when filter changes
  useEffect(() => { setItemIdx(0); }, [pillarFilter]);

  // ---- Auto-pilot: advance slides/items every cycleSpeed seconds ----
  const advanceAutoPilot = useCallback(() => {
    setSlide(currentSlide => {
      // On checklist slide (2), advance items first; only advance slide when at last item
      if (currentSlide === 2 && filteredItems.length > 0) {
        let advanced = false;
        setItemIdx(currentIdx => {
          if (currentIdx < filteredItems.length - 1) {
            advanced = true;
            return currentIdx + 1;
          }
          return 0; // reset for next cycle through this slide
        });
        if (advanced) return currentSlide;
      }
      // Wrap to slide 0 after last
      const next = (currentSlide + 1) % SLIDES.length;
      // Reset itemIdx when entering checklist slide fresh
      if (next === 2) setItemIdx(0);
      return next;
    });
  }, [filteredItems.length]);

  const stopAutoPilot = useCallback(() => {
    setAutoPilot(false);
  }, []);

  // Tick driver — runs while autoPilot is true and tab is visible
  useEffect(() => {
    if (!autoPilot) {
      setTickRemaining(0);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    setTickRemaining(cycleSpeed);
    tickRef.current = setInterval(() => {
      setTickRemaining(prev => {
        if (prev <= 1) {
          advanceAutoPilot();
          return cycleSpeed;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
  }, [autoPilot, cycleSpeed, advanceAutoPilot]);

  // Pause when tab hidden (don't auto-resume — only if user re-toggles)
  useEffect(() => {
    if (!autoPilot) return;
    const onVisChange = () => { if (document.hidden) stopAutoPilot(); };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, [autoPilot, stopAutoPilot]);

  // Silent data refresh every 5 minutes while autoPilot is on (24/7 lobby use case)
  useEffect(() => {
    if (!autoPilot) {
      if (refreshRef.current) { clearInterval(refreshRef.current); refreshRef.current = null; }
      return;
    }
    refreshRef.current = setInterval(() => { fetchAll(true); }, SILENT_REFRESH_INTERVAL_MS);
    return () => {
      if (refreshRef.current) { clearInterval(refreshRef.current); refreshRef.current = null; }
    };
  }, [autoPilot, fetchAll, SILENT_REFRESH_INTERVAL_MS]);

  // Persist user's preferred speed
  useEffect(() => {
    localStorage.setItem('inspectorMode.cycleSpeed', String(cycleSpeed));
  }, [cycleSpeed]);

  const toggleAutoPilot = () => setAutoPilot(v => !v);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onExit?.(); return; }
      if (e.key === 'ArrowLeft') {
        if (autoPilot) stopAutoPilot();
        if (slide === 2 && data?.items?.length) setItemIdx(i => Math.max(0, i - 1));
        else setSlide(s => Math.max(0, s - 1));
      } else if (e.key === 'ArrowRight') {
        if (autoPilot) stopAutoPilot();
        if (slide === 2 && data?.items?.length) setItemIdx(i => Math.min(filteredItems.length - 1, i + 1));
        else setSlide(s => Math.min(SLIDES.length - 1, s + 1));
      } else if (e.key === 'f' || e.key === 'F') {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
        else document.exitFullscreen?.();
      } else if (e.key === 'p' || e.key === 'P') {
        window.print();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide, data, itemIdx, onExit, autoPilot, stopAutoPilot]);

  // Auto-hide cursor after 3s idle
  useEffect(() => {
    const onMove = () => {
      setCursorHidden(false);
      if (cursorTimer.current) clearTimeout(cursorTimer.current);
      cursorTimer.current = setTimeout(() => setCursorHidden(true), 3000);
    };
    window.addEventListener('mousemove', onMove);
    onMove();
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (cursorTimer.current) clearTimeout(cursorTimer.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center" data-testid="inspector-mode-loading">
        <Loader2 className="w-12 h-12 text-violet-400 animate-spin" />
      </div>
    );
  }

  const meta = data?.document || {};
  const r = readiness?.readiness || { pct: 0, label: 'N0' };
  const overall = score?.overall || { pct: 0, label: 'N0', items_assessed: 0, items_total: 0 };
  const pillars = score?.pillars || {};

  return (
    <div
      className={`fixed inset-0 bg-gradient-to-br from-black via-slate-950 to-black text-white overflow-hidden ${cursorHidden ? 'cursor-none' : ''}`}
      data-testid="inspector-mode-page"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-violet-300" />
          <div className="text-xs uppercase tracking-[0.2em] text-violet-200">{t('inspectorMode.title')}</div>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {t('inspectorMode.liveData')}
          </span>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleLanguage}
            className="text-slate-300 hover:text-white h-8 text-[11px]"
            data-testid="inspector-language-toggle"
          >
            <Languages className="w-3.5 h-3.5 mr-1" />
            {i18n.language === 'ar' ? 'EN' : 'AR'}
          </Button>
          <div className="flex items-center bg-slate-900/60 border border-slate-800 rounded-md overflow-hidden" data-testid="inspector-autopilot-group">
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleAutoPilot}
              className={`h-8 text-[11px] rounded-none ${autoPilot ? 'bg-amber-500/20 text-amber-200' : 'text-slate-300 hover:text-white'}`}
              data-testid="inspector-autopilot-toggle"
            >
              {autoPilot
                ? <><Pause className="w-3.5 h-3.5 mr-1" /> {t('inspectorMode.autopilot')} · {tickRemaining}s</>
                : <><Play className="w-3.5 h-3.5 mr-1" /> {t('inspectorMode.autopilot')}</>}
            </Button>
            <select
              value={cycleSpeed}
              onChange={(e) => setCycleSpeed(parseInt(e.target.value, 10))}
              className="h-8 bg-transparent border-l border-slate-800 px-2 text-[11px] text-slate-300 focus:outline-none focus:text-white cursor-pointer"
              data-testid="inspector-autopilot-speed"
              aria-label="Cycle speed"
            >
              {SPEED_OPTIONS.map(s => (
                <option key={s} value={s} className="bg-slate-900">{s}s</option>
              ))}
            </select>
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={() => fetchAll(true)}
            className="text-slate-300 hover:text-white h-8 text-[11px]"
            data-testid="inspector-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {t('inspectorMode.refresh')}
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => {
              if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
              else document.exitFullscreen?.();
            }}
            className="text-slate-300 hover:text-white h-8 text-[11px]"
            data-testid="inspector-fullscreen-btn"
          >
            <Maximize2 className="w-3.5 h-3.5 mr-1" /> {t('inspectorMode.fullscreen')} <kbd className="ml-1 px-1 py-0.5 rounded bg-slate-800 text-[9px]">F</kbd>
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => window.print()}
            className="text-slate-300 hover:text-white h-8 text-[11px]"
            data-testid="inspector-print-btn"
          >
            <Printer className="w-3.5 h-3.5 mr-1" /> {t('inspectorMode.print')} <kbd className="ml-1 px-1 py-0.5 rounded bg-slate-800 text-[9px]">P</kbd>
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={onExit}
            className="border-rose-500/30 text-rose-200 hover:bg-rose-500/10 h-8 text-[11px]"
            data-testid="inspector-exit-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" /> {t('inspectorMode.exit')} <kbd className="ml-1 px-1 py-0.5 rounded bg-slate-800 text-[9px]">Esc</kbd>
          </Button>
        </div>
      </div>

      {/* Main slide area */}
      <div className="absolute inset-0 pt-16 pb-20 flex items-center justify-center">
        {/* Slide 0: Cover */}
        {slide === 0 && (
          <div className="text-center max-w-4xl mx-auto px-12 animate-in fade-in duration-700" data-testid="inspector-slide-cover">
            <div className="text-[10px] uppercase tracking-[0.4em] text-violet-300 mb-4" dir="ltr">
              {meta.ref} · {meta.revision} · {meta.date}
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {metadata?.certificate_holder_name || t('inspectorMode.cover.certificateHolderFallback')}
            </h1>
            <div className="text-lg text-slate-400 mb-1">
              {t('inspectorMode.cover.aoc')}: <span className="text-slate-200 font-mono" dir="ltr">{metadata?.aoc_number || t('inspectorMode.cover.aocFallback')}</span>
              {metadata?.post_holder_name && (
                <> &nbsp;·&nbsp; {t('inspectorMode.cover.ae')}: <span className="text-slate-200">{metadata.post_holder_name}</span></>
              )}
            </div>
            <div className="text-sm text-slate-500 mb-12">{t('inspectorMode.cover.subtitle')}</div>

            <div className="inline-block">
              <div
                className="relative"
                data-testid="inspector-readiness-score"
                dir="ltr"
              >
                <div className={`text-[140px] leading-none font-bold tabular-nums ${
                  r.pct >= 75 ? 'text-emerald-400' :
                  r.pct >= 50 ? 'text-sky-400' :
                  r.pct >= 25 ? 'text-amber-400' :
                  'text-rose-400'
                }`}>
                  {r.pct}<span className="text-5xl">%</span>
                </div>
                <div className="text-2xl text-slate-300 -mt-2">{r.label}</div>
                <div className="text-sm text-slate-500 mt-1">{t('inspectorMode.cover.compositeReadiness')}</div>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 text-left max-w-2xl mx-auto">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{t('inspectorMode.cover.itemsAssessed')}</div>
                <div className="text-2xl font-bold text-white tabular-nums mt-0.5" dir="ltr">{overall.items_assessed} / {overall.items_total}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{t('inspectorMode.cover.maturity')}</div>
                <div className="text-2xl font-bold text-white tabular-nums mt-0.5" dir="ltr">{overall.pct}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{t('inspectorMode.cover.linkedCapas')}</div>
                <div className="text-2xl font-bold text-white tabular-nums mt-0.5" dir="ltr">{linked?.total || 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* Slide 1: Pillar gauges */}
        {slide === 1 && (
          <div className="max-w-7xl mx-auto px-12 w-full animate-in fade-in duration-500" data-testid="inspector-slide-pillars">
            <h2 className="text-3xl font-bold text-white mb-2">{t('inspectorMode.pillars.title')}</h2>
            <p className="text-slate-400 mb-8">{t('inspectorMode.pillars.description')}</p>
            <div className="grid grid-cols-3 gap-6">
              {PILLAR_ORDER.map((p) => {
                const pd = pillars[p] || { pct: 0, label: 'N0', items: 0, max: 0, assessed: 0 };
                return (
                  <MaturityGauge
                    key={p}
                    name={p}
                    shortName={pillarShort(p)}
                    pct={pd.pct}
                    label={pd.label}
                    items={pd.assessed}
                    max={pd.items}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Slide 2: Items carousel */}
        {slide === 2 && filteredItems.length > 0 && (
          <div className="max-w-6xl w-full mx-auto px-12 animate-in fade-in duration-300" data-testid="inspector-slide-items">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-violet-300">
                  {t('inspectorMode.items.itemOf', { idx: itemIdx + 1, total: filteredItems.length })}
                </div>
                <h2 className="text-2xl font-bold text-white mt-0.5">
                  <span dir="ltr" className="font-mono">{filteredItems[itemIdx].id}</span> — {pillarShort(filteredItems[itemIdx].pillar)}
                </h2>
              </div>
              <select
                value={pillarFilter}
                onChange={(e) => setPillarFilter(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-200"
                data-testid="inspector-pillar-filter"
              >
                <option value="all">{t('inspectorMode.items.allPillars', { count: data.items.length })}</option>
                {PILLAR_ORDER.map(p => {
                  const n = data.items.filter(i => i.pillar === p).length;
                  if (!n) return null;
                  return <option key={p} value={p}>{pillarShort(p)} ({n})</option>;
                })}
              </select>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-8 min-h-[380px] flex flex-col">
              <div className="text-sm text-slate-400 font-mono mb-2" dir="ltr">{filteredItems[itemIdx].gacar}</div>
              <p className="text-xl text-white leading-relaxed mb-6">{filteredItems[itemIdx].requirement}</p>

              <div className="flex flex-wrap gap-2 mb-6">
                <PSOEPill label={t('inspectorMode.items.psoePresent')} on={!!filteredItems[itemIdx].assessment.present} />
                <PSOEPill label={t('inspectorMode.items.psoeSuitable')} on={!!filteredItems[itemIdx].assessment.suitable} />
                <PSOEPill label={t('inspectorMode.items.psoeOperating')} on={!!filteredItems[itemIdx].assessment.operating} />
                <PSOEPill label={t('inspectorMode.items.psoeEffective')} on={!!filteredItems[itemIdx].assessment.effective} />
                {filteredItems[itemIdx].assessment.bulk_drafted && (
                  <Badge className="border border-violet-500/40 bg-violet-500/10 text-violet-300 text-[10px] uppercase tracking-wider">
                    <Sparkles className="w-3 h-3 mr-1" /> {t('inspectorMode.items.aiDrafted')}
                  </Badge>
                )}
              </div>

              {filteredItems[itemIdx].assessment.how_achieved && (() => {
                const currentItem = filteredItems[itemIdx];
                const tr = translations[currentItem.id];
                const isShowingTranslation =
                  tr && tr.text && tr.sourceText === currentItem.assessment.how_achieved;
                const isTranslating = translatingItemId === currentItem.id;
                const targetLang = i18n.language === 'ar' ? 'ar' : 'en';
                const buttonLabel = isShowingTranslation
                  ? t('inspectorMode.items.showOriginal')
                  : isTranslating
                    ? t('inspectorMode.items.translating')
                    : (targetLang === 'ar'
                      ? t('inspectorMode.items.translateToArabic')
                      : t('inspectorMode.items.translateToEnglish'));
                return (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 flex-1 overflow-y-auto" data-testid="inspector-how-achieved-panel">
                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300">{t('inspectorMode.items.howAchieved')}</div>
                        {isShowingTranslation && (
                          <Badge className="border border-violet-500/40 bg-violet-500/10 text-violet-200 text-[10px] uppercase tracking-wider" data-testid="inspector-translated-badge">
                            <Languages className="w-3 h-3 mr-1" />
                            {t('inspectorMode.items.translatedBadge')}
                            {tr.cached && <span className="ml-1 opacity-70">· {t('inspectorMode.items.cachedBadge')}</span>}
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isTranslating}
                        onClick={() => { if (autoPilot) stopAutoPilot(); handleTranslate(currentItem); }}
                        className="h-7 text-[11px] text-slate-300 hover:text-white border border-slate-700 hover:border-violet-500/40"
                        data-testid="inspector-translate-btn"
                      >
                        {isTranslating
                          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          : <Languages className="w-3.5 h-3.5 mr-1" />}
                        {buttonLabel}
                      </Button>
                    </div>
                    <p
                      className="text-sm text-slate-200 leading-relaxed whitespace-pre-line"
                      dir={isShowingTranslation ? (tr.lang === 'ar' ? 'rtl' : 'ltr') : 'auto'}
                      data-testid="inspector-how-achieved-text"
                    >
                      {isShowingTranslation ? tr.text : currentItem.assessment.how_achieved}
                    </p>
                  </div>
                );
              })()}

              {filteredItems[itemIdx].evidence && filteredItems[itemIdx].evidence.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2 items-center">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500"><Activity className="w-3 h-3 inline mr-1" /> {t('inspectorMode.items.liveEvidence')}</span>
                  {filteredItems[itemIdx].evidence.map((e, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className={`border-slate-700 ${e.count > 0 ? 'text-sky-300 bg-sky-500/10' : 'text-slate-500'}`}
                    >
                      <span className="tabular-nums mr-1.5" dir="ltr">{e.count}</span>{e.label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Item-level navigation */}
            <div className="flex items-center justify-between mt-4">
              <Button
                variant="outline" size="sm"
                disabled={itemIdx === 0}
                onClick={() => { if (autoPilot) stopAutoPilot(); setItemIdx(i => Math.max(0, i - 1)); }}
                className="border-slate-700 text-slate-200"
                data-testid="inspector-item-prev"
              >
                {isRTL
                  ? <>{t('inspectorMode.items.prev')} <ChevronRight className="w-4 h-4 ml-1" /></>
                  : <><ChevronLeft className="w-4 h-4 mr-1" /> {t('inspectorMode.items.prev')}</>}
              </Button>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('inspectorMode.items.navHint')}</div>
              <Button
                variant="outline" size="sm"
                disabled={itemIdx >= filteredItems.length - 1}
                onClick={() => { if (autoPilot) stopAutoPilot(); setItemIdx(i => Math.min(filteredItems.length - 1, i + 1)); }}
                className="border-slate-700 text-slate-200"
                data-testid="inspector-item-next"
              >
                {isRTL
                  ? <><ChevronLeft className="w-4 h-4 mr-1" /> {t('inspectorMode.items.next')}</>
                  : <>{t('inspectorMode.items.next')} <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Slide 3: Linked CAPAs timeline */}
        {slide === 3 && (
          <div className="max-w-5xl w-full mx-auto px-12 animate-in fade-in duration-500" data-testid="inspector-slide-capas">
            <h2 className="text-3xl font-bold text-white mb-2">{t('inspectorMode.capas.title')}</h2>
            <p className="text-slate-400 mb-8">
              {t('inspectorMode.capas.subtitle', { total: linked?.total || 0 })}
            </p>

            {(!linked?.groups || linked.groups.length === 0) ? (
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">{t('inspectorMode.capas.noneYet')}</p>
              </div>
            ) : (
              <div className="space-y-6 max-h-[550px] overflow-y-auto pr-2">
                {linked.groups.map((g) => (
                  <div key={g.month} className="relative" data-testid={`inspector-capa-month-${g.month}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-3 h-3 rounded-full bg-violet-400" />
                      <div className="text-lg font-semibold text-white">{g.label}</div>
                      <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{g.count} {t('inspectorMode.capas.countSuffix')}</Badge>
                    </div>
                    <div className={`${isRTL ? 'mr-1.5 pr-5 border-r' : 'ml-1.5 pl-5 border-l'} border-slate-800 space-y-2`}>
                      {g.items.map((it) => (
                        <div
                          key={it.capa_number}
                          className="rounded-lg border border-slate-800 bg-slate-950/40 p-3"
                        >
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-[280px]">
                              <div className="font-mono text-xs text-violet-300 mb-1" dir="ltr">{it.capa_number}</div>
                              <div className="text-sm text-slate-200">{it.title}</div>
                              <div className="text-[11px] text-slate-500 mt-1">
                                {t('inspectorMode.capas.evidenceAtConversion')}: {it.evidence_at_conversion || '—'} → {t('inspectorMode.capas.target')}: {it.target_at_conversion || '—'}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge
                                className={
                                  it.is_overdue ? 'bg-rose-500/15 border-rose-500/40 text-rose-300' :
                                  it.status === 'closed' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' :
                                  it.status === 'in_progress' ? 'bg-sky-500/15 border-sky-500/40 text-sky-300' :
                                  'bg-amber-500/15 border-amber-500/40 text-amber-300'
                                }
                              >
                                {it.is_overdue ? t('inspectorMode.capas.statusOverdue') :
                                  it.status === 'closed' ? t('inspectorMode.capas.statusClosed') :
                                  it.status === 'in_progress' ? t('inspectorMode.capas.statusInProgress') :
                                  t('inspectorMode.capas.statusOpen')}
                              </Badge>
                              <div className="text-[10px] text-slate-500 tabular-nums" dir="ltr">
                                {t('inspectorMode.capas.due')} {(it.due_date || '').slice(0, 10) || '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state for items slide when filter excludes everything */}
        {slide === 2 && filteredItems.length === 0 && (
          <div className="text-center text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <p>{t('inspectorMode.items.noneInPillar')}</p>
          </div>
        )}
      </div>

      {/* Bottom slide indicator + nav */}
      <div className="absolute bottom-0 left-0 right-0 z-30 p-4">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="ghost" size="sm"
            disabled={slide === 0}
            onClick={() => { if (autoPilot) stopAutoPilot(); setSlide(s => Math.max(0, s - 1)); }}
            className="text-slate-400 hover:text-white"
            data-testid="inspector-slide-prev"
          >
            {isRTL
              ? <>{t('inspectorMode.items.prev')} <ChevronRight className="w-4 h-4 ml-1" /></>
              : <><ChevronLeft className="w-4 h-4 mr-1" /> {t('inspectorMode.items.prev')}</>}
          </Button>

          <div className="flex items-center gap-2" data-testid="inspector-slide-indicator">
            {SLIDES.map((s, i) => {
              const Icon = s.icon;
              const active = i === slide;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { if (autoPilot) stopAutoPilot(); setSlide(i); }}
                  data-testid={`inspector-slide-dot-${s.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition-all ${
                    active
                      ? 'bg-violet-500/20 border border-violet-500/40 text-violet-200'
                      : 'border border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {s.label}
                </button>
              );
            })}
          </div>

          <Button
            variant="ghost" size="sm"
            disabled={slide === SLIDES.length - 1}
            onClick={() => { if (autoPilot) stopAutoPilot(); setSlide(s => Math.min(SLIDES.length - 1, s + 1)); }}
            className="text-slate-400 hover:text-white"
            data-testid="inspector-slide-next"
          >
            {isRTL
              ? <><ChevronLeft className="w-4 h-4 mr-1" /> {t('inspectorMode.items.next')}</>
              : <>{t('inspectorMode.items.next')} <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>

        {/* Thin progress strip */}
        <div className="mt-3 h-0.5 bg-slate-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
            style={{ width: `${((slide + 1) / SLIDES.length) * 100}%` }}
          />
        </div>

        {/* Auto-pilot countdown bar — only when running */}
        {autoPilot && (
          <div className="mt-1.5 h-0.5 bg-slate-900 rounded-full overflow-hidden" data-testid="inspector-autopilot-countdown">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500"
              style={{
                width: `${((cycleSpeed - tickRemaining) / cycleSpeed) * 100}%`,
                transition: 'width 1s linear',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
